import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function AddClientDialog({ open, onOpenChange, onCreated }) {
    const [form, setForm] = useState({ company_name: "", contact_name: "", contact_email: "", project_name: "" });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/clients", form);
            toast.success(`${data.company_name} added`, { description: "Portal is ready to share." });
            setForm({ company_name: "", contact_name: "", contact_email: "", project_name: "" });
            onCreated?.(data);
        } catch (err) {
            toast.error("Could not add client", { description: formatApiErrorDetail(err.response?.data?.detail) });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[480px] rounded-xl border-slate-200">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-tight">Add a new client</DialogTitle>
                    <DialogDescription className="text-slate-500">
                        We'll spin up their portal instantly. You can connect tools later.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 pt-2">
                    <div className="space-y-2">
                        <Label htmlFor="company_name" className="text-slate-700">Company name</Label>
                        <Input
                            id="company_name"
                            data-testid="add-client-company"
                            value={form.company_name}
                            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                            placeholder="Northwind Coffee"
                            required
                            className="ring-terra"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label htmlFor="contact_name" className="text-slate-700">Contact name</Label>
                            <Input
                                id="contact_name"
                                data-testid="add-client-contact-name"
                                value={form.contact_name}
                                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                                placeholder="Marta Bianchi"
                                required
                                className="ring-terra"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="contact_email" className="text-slate-700">Contact email</Label>
                            <Input
                                id="contact_email"
                                type="email"
                                data-testid="add-client-contact-email"
                                value={form.contact_email}
                                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                                placeholder="marta@northwind.co"
                                required
                                className="ring-terra"
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="project_name" className="text-slate-700">Project name</Label>
                        <Input
                            id="project_name"
                            data-testid="add-client-project"
                            value={form.project_name}
                            onChange={(e) => setForm({ ...form, project_name: e.target.value })}
                            placeholder="Website Redesign"
                            required
                            className="ring-terra"
                        />
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors"
                            data-testid="add-client-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="add-client-submit"
                            className="btn-terra rounded-lg px-4 py-2 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-70"
                        >
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                            Create portal
                        </button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
